import React, { useState, useEffect, useCallback } from 'react';
import LayoutWrapper from '../../../../shared/components/LayoutWrapper';
import { useAuth } from '../../../../shared/hooks/useAuth';
import { API_CONFIG, API_ENDPOINTS } from '../../../../config/api';

interface Customer {
  Oid: string;
  CustomerID: string;
  CustomerName: string;
  [key: string]: any;
}

const ExcelUploadPage: React.FC = () => {
  const { userAuth } = useAuth();
  
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerOid, setSelectedCustomerOid] = useState<string>('');
  const [fileType, setFileType] = useState<'purchase' | 'sales' | ''>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch customers list
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('novaqToken');
      if (!token) {
        throw new Error('NovaQ token олдсонгүй. Дахин нэвтэрнэ үү.');
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.CUSTOMERS.BASE}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Серверийн алдаа: ${response.status}`);
      }

      const result = await response.json();

      let customersList: Customer[] = [];
      if (result?.success && result?.data) {
        customersList = result.data;
      } else if (Array.isArray(result)) {
        customersList = result;
      } else {
        throw new Error(result?.message || 'Харилцагчдын жагсаалт татахад алдаа гарлаа');
      }

      // CustomerName-аар эрэмбэлэх
      customersList.sort((a, b) => {
        const nameA = (a.CustomerName || '').toLowerCase();
        const nameB = (b.CustomerName || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setCustomers(customersList);
    } catch (err: any) {
      console.error('❌ Customers fetch error:', err);
      setError(err?.message || 'Харилцагчдын жагсаалт татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedExtensions = ['.xlsx', '.xls', '.csv'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!allowedExtensions.includes(fileExtension)) {
        setError('Зөвшөөрөгдөөгүй файлын формат. Зөвшөөрөгдсөн формат: .xlsx, .xls, .csv');
        setSelectedFile(null);
        return;
      }
      
      setSelectedFile(file);
      setError(null);
      
      // Auto-detect file type from filename
      const fileName = file.name.toLowerCase();
      if (fileName.includes('niit_hudaldan_avalt') || 
          fileName.includes('hudaldan_avalt') || 
          fileName.includes('hudaldan') ||
          fileName.includes('purchase')) {
        setFileType('purchase');
      } else if (fileName.includes('niit_borluulalt') || 
                 fileName.includes('borluulalt') || 
                 fileName.includes('sales')) {
        setFileType('sales');
      }
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Файл сонгоно уу');
      return;
    }

    if (!fileType) {
      setError('Файлын төрөл сонгоно уу');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('novaqToken');
      if (!token) {
        throw new Error('NovaQ token олдсонгүй. Дахин нэвтэрнэ үү.');
      }

      const formData = new FormData();
      formData.append('file', selectedFile);
      if (fileType) {
        formData.append('type', fileType);
      }
      if (selectedCustomerOid) {
        formData.append('customerOid', selectedCustomerOid);
      }

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/taxsystem/ebarimt/accountant/ebarimt/upload-file`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.message || `Серверийн алдаа: ${response.status}`);
      }

      const result = await response.json();

      if (result?.success) {
        setSuccess(
          `Файл амжилттай upload хийгдлээ. ${result.inserted || 0} мөр нэмэгдсэн, ${result.errors || 0} алдаа гарлаа`
        );
        // Reset form
        setSelectedFile(null);
        setFileType('');
        setSelectedCustomerOid('');
        // Reset file input
        const fileInput = document.getElementById('file-input') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      } else {
        throw new Error(result?.message || 'Файл upload хийхэд алдаа гарлаа');
      }
    } catch (err: any) {
      console.error('❌ File upload error:', err);
      setError(err?.message || 'Файл upload хийхэд алдаа гарлаа');
    } finally {
      setUploading(false);
    }
  };

  return (
    <LayoutWrapper userAuth={userAuth}>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Excel файл Upload</h1>
          <p className="text-gray-600 mt-2">
            Excel/CSV файл upload хийж, EBarimtDB database-д өгөгдөл нэмэх
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          {/* Customer Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="customer-select">
              Харилцагч сонгох (Сонгох нь заавал биш)
            </label>
            <select
              id="customer-select"
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedCustomerOid}
              onChange={(e) => setSelectedCustomerOid(e.target.value)}
              disabled={loading}
            >
              <option value="">-- Бүх харилцагч --</option>
              {customers.map((customer) => (
                <option key={customer.Oid} value={customer.Oid}>
                  {customer.CustomerID} - {customer.CustomerName}
                </option>
              ))}
            </select>
          </div>

          {/* File Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Файлын төрөл сонгох <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="fileType"
                  value="purchase"
                  checked={fileType === 'purchase'}
                  onChange={(e) => setFileType(e.target.value as 'purchase')}
                  className="mr-2"
                />
                <span>Худалдан авалт (Purchase)</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="fileType"
                  value="sales"
                  checked={fileType === 'sales'}
                  onChange={(e) => setFileType(e.target.value as 'sales')}
                  className="mr-2"
                />
                <span>Борлуулалт (Sales)</span>
              </label>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Файлын нэрээс автоматаар тодорхойлогдоно (Niit_hudaldan_avalt → Purchase, Niit_borluulalt → Sales)
            </p>
          </div>

          {/* File Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="file-input">
              Excel/CSV файл сонгох
            </label>
            <input
              id="file-input"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={uploading}
            />
            {selectedFile && (
              <div className="mt-2 p-3 bg-blue-50 rounded border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Сонгосон файл:</strong> {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                </p>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700">
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mb-4 p-3 rounded bg-green-50 border border-green-200 text-green-700">
              {success}
            </div>
          )}

          {/* Upload Button */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleUpload}
              disabled={!selectedFile || !fileType || uploading}
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {uploading ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Upload хийж байна...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Upload хийх
                </>
              )}
            </button>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-gray-50 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Заавар</h2>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Зөвшөөрөгдсөн файлын формат: .xlsx, .xls, .csv</li>
            <li>Файлын хамгийн их хэмжээ: 10MB</li>
            <li>Худалдан авалтын файл: "Niit_hudaldan_avalt" гэсэн нэртэй файл → EbarimtPurchaseHeader table-д insert хийгдэнэ</li>
            <li>Борлуулалтын файл: "Niit_borluulalt" гэсэн нэртэй файл → EbarimtSales table-д insert хийгдэнэ</li>
            <li>Файлын нэрээс автоматаар төрөл тодорхойлогдоно, эсвэл гараар сонгож болно</li>
            <li>Харилцагч сонгох нь заавал биш - сонгохгүй бол бүх харилцагчийн өгөгдөл боловсруулагдана</li>
          </ul>
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default ExcelUploadPage;

